// Version history — what's new in each release.

import { Sparkles, Zap, Shield, FileSearch } from "lucide-react";

interface Release {
  version: string;
  date: string;
  title: string;
  highlights: string[];
  icon: typeof Sparkles;
}

const RELEASES: Release[] = [
  {
    version: "2.2.0",
    date: "April 2026",
    title: "ATS Resume Scorer",
    highlights: [
      "Web portal: score your resume against any job description",
      "Use profile data or upload another resume (parsed via parse-resume)",
      "Score gauge, verdict, keyword gaps with importance, section rewrites",
      "New score-resume edge function (Claude)",
    ],
    icon: FileSearch,
  },
  {
    version: "2.1.1",
    date: "April 2026",
    title: "Extended Autofill & Web Portal",
    highlights: [
      "Full address autofill — street, city, state, postcode, country",
      "New fields — date of birth, gender, work authorization, years of experience, GitHub",
      "PDF cover letter downloads (replacing .doc)",
      "Web Portal with full profile management",
      "Session handoff — sign in once, access everywhere",
      "Migrated to independent Supabase infrastructure",
      "AI powered by Claude (Anthropic)",
    ],
    icon: Zap,
  },
  {
    version: "2.0.0",
    date: "March 2026",
    title: "The Big Overhaul",
    highlights: [
      "24-hour free access — no account needed to start",
      "30-second quick setup replaces 6-step onboarding",
      "Soft nudge system instead of hard trial wall",
      "Removed sign-up requirement for first use",
      "Improved scam filtering in companion tools",
      "Cleaner Chrome Web Store listing",
    ],
    icon: Sparkles,
  },
  {
    version: "1.0.0",
    date: "February 2026",
    title: "Where It All Started",
    highlights: [
      "AI cover letter generator with real profile data",
      "Resume upload with AI extraction",
      "Custom Q&A answers from job descriptions",
      "Application tracker with CTC & currency support",
      "Complete profile management",
      "Dark/light mode support",
      "7-day free trial",
    ],
    icon: Shield,
  },
];

export default function ChangelogTab() {
  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h3 className="font-display font-bold text-lg text-foreground">
          What&apos;s New
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          A look at how Jobily has evolved.
        </p>
      </div>

      <div className="space-y-6">
        {RELEASES.map((release, i) => {
          const Icon = release.icon;
          return (
            <div key={release.version} className="relative">
              {/* Timeline connector */}
              {i < RELEASES.length - 1 && (
                <div className="absolute left-[19px] top-[44px] bottom-[-24px] w-px bg-border" />
              )}

              <div className="flex gap-4">
                {/* Version dot */}
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 z-10">
                  <Icon className="w-5 h-5 text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1 pb-2">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm text-foreground">
                      v{release.version}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {release.date}
                    </span>
                  </div>
                  <h4 className="font-display font-semibold text-sm text-foreground mt-0.5">
                    {release.title}
                  </h4>
                  <ul className="mt-2 space-y-1">
                    {release.highlights.map((h, j) => (
                      <li
                        key={j}
                        className="text-xs text-muted-foreground flex items-start gap-2"
                      >
                        <span className="text-primary mt-0.5">·</span>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg bg-muted/50 border border-border p-3 text-center">
        <p className="text-xs text-muted-foreground">
          Built with care by{" "}
          <span className="font-medium text-foreground">
            Squareone International
          </span>
        </p>
      </div>
    </div>
  );
}
